import React, { useState, useEffect, useMemo } from 'react';
import { jobAssignmentsAPI, timesheetsAPI, wageSchedulesAPI, jobsAPI, teamMembersAPI, clockAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, parseISO } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import TeamMemberSearchSelect from '../components/TeamMemberSearchSelect';
import {
  Clock,
  Users,
  Calendar,
  Plus,
  X,
  Save,
  AlertTriangle,
  CheckCircle2,
  FileText,
  ClipboardList,
  Coins,
  Trash2,
  Edit,
  Eye,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  MapPin,
  Play,
  Square,
} from 'lucide-react';

const TimeAttendance = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('assignments');
  const [jobs, setJobs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [wageSchedules, setWageSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Filters state
  const [filterMemberId, setFilterMemberId] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const [filterPeriodStart, setFilterPeriodStart] = useState('');
  const [filterPeriodEnd, setFilterPeriodEnd] = useState('');
  
  // Clock In/Out state
  const [clockEntries, setClockEntries] = useState([]);
  const [clockExceptions, setClockExceptions] = useState([]);
  const [selectedMemberForClock, setSelectedMemberForClock] = useState('');
  const [clockStatus, setClockStatus] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  const [assignmentForm, setAssignmentForm] = useState({
    job_id: '',
    member_id: '',
    assignment_start: '',
    assignment_end: '',
    planned_hours_per_day: 8,
    notes: '',
  });

  const [timesheetForm, setTimesheetForm] = useState({
    member_id: '',
    job_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    hours_worked: 8,
    overtime_hours: 0,
    overtime_rate: 1.5,
    description: '',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  // Filtered assignments based on search criteria
  const filteredAssignments = useMemo(() => {
    return assignments.filter(assignment => {
      if (filterMemberId && assignment.member_id !== filterMemberId) return false;
      if (filterJobId && assignment.job_id !== filterJobId) return false;
      if (filterPeriodStart && new Date(assignment.assignment_end) < new Date(filterPeriodStart)) return false;
      if (filterPeriodEnd && new Date(assignment.assignment_start) > new Date(filterPeriodEnd)) return false;
      return true;
    });
  }, [assignments, filterMemberId, filterJobId, filterPeriodStart, filterPeriodEnd]);

  const clearFilters = () => {
    setFilterMemberId('');
    setFilterJobId('');
    setFilterPeriodStart('');
    setFilterPeriodEnd('');
  };

  const hasActiveFilters = filterMemberId || filterJobId || filterPeriodStart || filterPeriodEnd;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'timesheets') {
      loadTimesheets();
    }
    if (activeTab === 'clock') {
      loadClockData();
    }
  }, [currentWeekStart, activeTab]);

  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  }, []);

  const loadData = async () => {
    try {
      const [jobsRes, membersRes, assignmentsRes, schedulesRes] = await Promise.all([
        jobsAPI.getAll(),
        teamMembersAPI.getAll(),
        jobAssignmentsAPI.getAll(),
        wageSchedulesAPI.getAll(),
      ]);
      setJobs(jobsRes.data);
      setTeamMembers(membersRes.data.filter(m => m.status === 'active'));
      setAssignments(assignmentsRes.data);
      setWageSchedules(schedulesRes.data);
      loadTimesheets();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadClockData = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [entriesRes, exceptionsRes] = await Promise.all([
        clockAPI.getEntries({ date: today }),
        clockAPI.getExceptions({ resolved: false }),
      ]);
      setClockEntries(entriesRes.data);
      setClockExceptions(exceptionsRes.data);
    } catch (error) {
      console.error('Failed to load clock data');
    }
  };

  const loadMemberClockStatus = async (memberId) => {
    if (!memberId) {
      setClockStatus(null);
      return;
    }
    try {
      setClockLoading(true);
      const res = await clockAPI.getMyStatus(memberId);
      setClockStatus(res.data);
    } catch (error) {
      toast.error('Failed to load clock status');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockIn = async (jobId) => {
    if (!selectedMemberForClock) {
      toast.error('Please select a team member');
      return;
    }
    try {
      setClockLoading(true);
      const res = await clockAPI.clockIn({
        member_id: selectedMemberForClock,
        job_id: jobId,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
      });
      
      if (res.data.outside_geofence) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      
      loadMemberClockStatus(selectedMemberForClock);
      loadClockData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!clockStatus?.active_entry) {
      toast.error('Not clocked in');
      return;
    }
    try {
      setClockLoading(true);
      const res = await clockAPI.clockOut(clockStatus.active_entry.id, {
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
      });
      
      if (res.data.outside_geofence) {
        toast.warning(`${res.data.message} - ${res.data.hours_worked} hours worked`);
      } else {
        toast.success(`${res.data.message} - ${res.data.hours_worked} hours worked`);
      }
      
      loadMemberClockStatus(selectedMemberForClock);
      loadClockData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockLoading(false);
    }
  };

  const handleCheckExceptions = async () => {
    try {
      const res = await clockAPI.checkExceptions();
      toast.success(res.data.message);
      loadClockData();
    } catch (error) {
      toast.error('Failed to check exceptions');
    }
  };

  const handleResolveException = async (exceptionId, notes) => {
    try {
      await clockAPI.resolveException(exceptionId, notes);
      toast.success('Exception resolved');
      loadClockData();
    } catch (error) {
      toast.error('Failed to resolve exception');
    }
  };

  const loadTimesheets = async () => {
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const res = await timesheetsAPI.getAll({
        start_date: currentWeekStart.toISOString(),
        end_date: weekEnd.toISOString(),
      });
      setTimesheets(res.data);
    } catch (error) {
      console.error('Failed to load timesheets');
    }
  };

  const handleCheckConflicts = async () => {
    if (!assignmentForm.job_id || !assignmentForm.member_id || !assignmentForm.assignment_start || !assignmentForm.assignment_end) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await jobAssignmentsAPI.checkConflicts(assignmentForm);
      if (res.data.has_conflicts) {
        setConflicts(res.data.conflicts);
        setShowConflictModal(true);
      } else {
        handleCreateAssignment();
      }
    } catch (error) {
      toast.error('Failed to check conflicts');
    }
  };

  const handleCreateAssignment = async () => {
    try {
      await jobAssignmentsAPI.create(assignmentForm);
      toast.success('Assignment created successfully');
      setShowAssignmentModal(false);
      setShowConflictModal(false);
      setAssignmentForm({
        job_id: '',
        member_id: '',
        assignment_start: '',
        assignment_end: '',
        planned_hours_per_day: 8,
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    try {
      await jobAssignmentsAPI.delete(id);
      toast.success('Assignment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const handleCreateTimesheet = async () => {
    if (!timesheetForm.member_id || !timesheetForm.job_id || !timesheetForm.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await timesheetsAPI.create(timesheetForm);
      toast.success('Timesheet entry created');
      setShowTimesheetModal(false);
      setTimesheetForm({
        member_id: '',
        job_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        hours_worked: 8,
        overtime_hours: 0,
        overtime_rate: 1.5,
        description: '',
      });
      loadTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet entry');
    }
  };

  const handleApproveTimesheet = async (id) => {
    try {
      await timesheetsAPI.update(id, { status: 'approved' });
      toast.success('Timesheet approved');
      loadTimesheets();
    } catch (error) {
      toast.error('Failed to approve timesheet');
    }
  };

  const handleGenerateWageSchedule = async () => {
    const periodStart = format(subWeeks(new Date(), 2), 'yyyy-MM-dd');
    const periodEnd = format(new Date(), 'yyyy-MM-dd');

    try {
      const res = await wageSchedulesAPI.generate(periodStart, periodEnd);
      toast.success(`Wage schedule generated: ${res.data.entries_count} entries, ${formatCurrency(res.data.total_amount)}`);
      loadData();
    } catch (error) {
      toast.error('Failed to generate wage schedule');
    }
  };

  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    paid: 'bg-purple-100 text-purple-700',
    draft: 'bg-slate-100 text-slate-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Time & Attendance
          </h1>
          <p className="text-slate-600">Manage resource assignments, timesheets, and wage schedules</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4 overflow-x-auto">
          {[
            { id: 'clock', label: 'Clock In/Out', icon: Play },
            { id: 'assignments', label: 'Resource Assignments', icon: Users },
            { id: 'timesheets', label: 'Timesheets', icon: Clock },
            { id: 'wages', label: 'Wage Schedules', icon: Coins },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Clock In/Out Tab */}
      {activeTab === 'clock' && (
        <div className="space-y-6">
          {/* Select Team Member */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Clock In/Out</h2>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-slate-700 mb-2">Select Team Member</label>
              <TeamMemberSearchSelect
                members={teamMembers}
                selectedMemberId={selectedMemberForClock}
                onSelect={(id) => {
                  setSelectedMemberForClock(id);
                  loadMemberClockStatus(id);
                }}
                placeholder="Search team member..."
              />
            </div>

            {/* Clock Status Display */}
            {selectedMemberForClock && clockStatus && (
              <div className="mt-6">
                {clockStatus.is_clocked_in ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-900">Currently Clocked In</p>
                        <p className="text-sm text-green-700">
                          {clockStatus.active_entry.job_code && `${clockStatus.active_entry.job_code} - `}
                          {clockStatus.active_entry.job_title}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-green-600">Clocked in at:</span>
                        <p className="font-medium text-green-900">
                          {format(new Date(clockStatus.active_entry.clock_in_time), 'HH:mm')}
                        </p>
                      </div>
                      {clockStatus.active_entry.clock_in_outside_geofence && (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Outside geofence ({clockStatus.active_entry.clock_in_distance_from_site}m)</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleClockOut}
                      disabled={clockLoading}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      <Square className="w-5 h-5" />
                      Clock Out
                    </button>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                        <Clock className="w-6 h-6 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Not Clocked In</p>
                        <p className="text-sm text-slate-600">Select a job to clock in</p>
                      </div>
                    </div>

                    {clockStatus.today_assignments?.length > 0 ? (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-slate-700">Today&apos;s Assignments:</p>
                        {clockStatus.today_assignments.map((assignment) => (
                          <div key={assignment.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                              <MapPin className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900">
                                  {assignment.job_code && <span className="text-blue-600 mr-2">{assignment.job_code}</span>}
                                  {assignment.job_title}
                                </p>
                                <p className="text-sm text-slate-500">{assignment.job_location}</p>
                                {assignment.geofence_latitude && (
                                  <p className="text-xs text-slate-400">
                                    Geofence: {assignment.geofence_radius}m radius
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleClockIn(assignment.job_id)}
                              disabled={clockLoading}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-4 py-2 rounded-lg transition-colors"
                            >
                              <Play className="w-4 h-4" />
                              Clock In
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500">
                        <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p>No assignments scheduled for today</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Location Status */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {userLocation ? (
                      <span className="text-green-600">Location access enabled</span>
                    ) : (
                      <span className="text-amber-600">Location access not available - geofence checks disabled</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Today's Clock Entries */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Today&apos;s Clock Entries</h3>
              <span className="text-sm text-slate-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</span>
            </div>
            {clockEntries.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Team Member</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Clock In</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Clock Out</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Hours</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {clockEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{entry.member_name}</td>
                      <td className="px-4 py-3">
                        {entry.job_code && <span className="text-xs font-mono text-blue-600 mr-2">{entry.job_code}</span>}
                        <span className="text-slate-900">{entry.job_title}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {entry.clock_in_time && format(new Date(entry.clock_in_time), 'HH:mm')}
                        {entry.clock_in_outside_geofence && (
                          <span className="ml-2 text-amber-500" title="Outside geofence">⚠️</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {entry.clock_out_time ? format(new Date(entry.clock_out_time), 'HH:mm') : '-'}
                        {entry.clock_out_outside_geofence && (
                          <span className="ml-2 text-amber-500" title="Outside geofence">⚠️</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {entry.hours_worked ? `${entry.hours_worked}h` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          entry.status === 'clocked_in' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {entry.status === 'clocked_in' ? 'Active' : 'Completed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No clock entries for today</p>
              </div>
            )}
          </div>

          {/* Exceptions */}
          {canEdit && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Exceptions
                </h3>
                <button
                  onClick={handleCheckExceptions}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Check for exceptions
                </button>
              </div>
              {clockExceptions.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {clockExceptions.map((exception) => (
                    <div key={exception.id} className="p-4 flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{exception.member_name}</p>
                        <p className="text-sm text-slate-600">{exception.description}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {exception.exception_type === 'not_clocked_in' && '🔴 Not clocked in by 10:00 AM'}
                          {exception.exception_type === 'not_clocked_out' && '🟠 Not clocked out by 6:00 PM'}
                          {exception.exception_type === 'outside_geofence' && '⚠️ Outside geofence'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          const notes = prompt('Resolution notes:');
                          if (notes) handleResolveException(exception.id, notes);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Resolve
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-300" />
                  <p>No unresolved exceptions</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resource Assignments Tab */}
      {activeTab === 'assignments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Team Member Job Assignments</h2>
            {canEdit && (
              <button
                onClick={() => setShowAssignmentModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Assignment
              </button>
            )}
          </div>

          {/* Filter Section */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="ml-auto text-xs text-blue-600 hover:text-blue-700"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Team Member</label>
                <TeamMemberSearchSelect
                  members={teamMembers}
                  selectedMemberId={filterMemberId}
                  onSelect={setFilterMemberId}
                  placeholder="Search member..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Job</label>
                <JobSearchSelect
                  jobs={jobs}
                  selectedJobId={filterJobId}
                  onSelect={setFilterJobId}
                  placeholder="Search job..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Period Start</label>
                <input
                  type="date"
                  value={filterPeriodStart}
                  onChange={(e) => setFilterPeriodStart(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Period End</label>
                <input
                  type="date"
                  value={filterPeriodEnd}
                  onChange={(e) => setFilterPeriodEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {filteredAssignments.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {hasActiveFilters ? 'No assignments match your filters' : 'No assignments yet'}
              </h3>
              <p className="text-slate-600">
                {hasActiveFilters 
                  ? 'Try adjusting your filter criteria.' 
                  : 'Assign team members to jobs to track planned hours and costs.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Team Member</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Period</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Planned Hours</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Planned Cost</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                    {canEdit && <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAssignments.map((assignment) => (
                    <tr key={assignment.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{assignment.member_name}</div>
                        <div className="text-xs text-slate-500">{formatCurrency(assignment.hourly_rate)}/hr</div>
                      </td>
                      <td className="px-4 py-3">
                        {assignment.job_code && (
                          <span className="text-xs font-mono text-blue-600 mr-2">{assignment.job_code}</span>
                        )}
                        <span className="text-slate-900">{assignment.job_title}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {format(parseISO(assignment.assignment_start), 'MMM dd')} - {format(parseISO(assignment.assignment_end), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {assignment.total_planned_hours} hrs
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {formatCurrency(assignment.total_planned_cost)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[assignment.status]}`}>
                          {assignment.status}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Timesheets Tab */}
      {activeTab === 'timesheets' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-slate-900">Weekly Timesheets</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-slate-600">
                  {format(currentWeekStart, 'MMM dd')} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM dd, yyyy')}
                </span>
                <button
                  onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowTimesheetModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Log Hours
            </button>
          </div>

          {timesheets.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No timesheet entries</h3>
              <p className="text-slate-600">Log hours worked for this week.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Team Member</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Hours</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Overtime</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Total</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                    {canEdit && <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {timesheets.map((ts) => (
                    <tr key={ts.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-900">
                        {format(parseISO(ts.date), 'EEE, MMM dd')}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">{ts.member_name}</td>
                      <td className="px-4 py-3">
                        {ts.job_code && <span className="text-xs font-mono text-blue-600 mr-2">{ts.job_code}</span>}
                        <span className="text-slate-600">{ts.job_title}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-900">{ts.hours_worked} hrs</td>
                      <td className="px-4 py-3 text-slate-600">
                        {ts.overtime_hours > 0 ? `${ts.overtime_hours} hrs @ ${ts.overtime_rate}x` : '-'}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {formatCurrency(ts.total_amount + (ts.overtime_amount || 0))}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[ts.status]}`}>
                          {ts.status}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 text-right">
                          {ts.status === 'pending' && (
                            <button
                              onClick={() => handleApproveTimesheet(ts.id)}
                              className="p-1 hover:bg-green-50 rounded text-green-600"
                              title="Approve"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Wage Schedules Tab */}
      {activeTab === 'wages' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Fortnightly Wage Schedules</h2>
            {canEdit && (
              <button
                onClick={handleGenerateWageSchedule}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                Generate Schedule
              </button>
            )}
          </div>

          {wageSchedules.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Coins className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No wage schedules</h3>
              <p className="text-slate-600">Generate a wage schedule from approved timesheets.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {wageSchedules.map((schedule) => (
                <div key={schedule.id} className="bg-white border border-slate-200 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {format(parseISO(schedule.period_start), 'MMM dd')} - {format(parseISO(schedule.period_end), 'MMM dd, yyyy')}
                      </h3>
                      <p className="text-sm text-slate-600">{schedule.entries?.length || 0} team members</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[schedule.status]}`}>
                        {schedule.status}
                      </span>
                      <p className="text-lg font-bold text-slate-900 mt-2">{formatCurrency(schedule.total_amount)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Regular Hours</p>
                      <p className="font-medium">{schedule.total_regular_hours} hrs</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Overtime Hours</p>
                      <p className="font-medium">{schedule.total_overtime_hours} hrs</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Total Amount</p>
                      <p className="font-medium">{formatCurrency(schedule.total_amount)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">New Resource Assignment</h2>
              <button onClick={() => setShowAssignmentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Team Member *</label>
                <TeamMemberSearchSelect
                  members={teamMembers}
                  selectedMemberId={assignmentForm.member_id}
                  onSelect={(memberId) => setAssignmentForm({ ...assignmentForm, member_id: memberId })}
                  placeholder="Search team member..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job *</label>
                <JobSearchSelect
                  jobs={jobs.filter(j => j.status !== 'completed')}
                  selectedJobId={assignmentForm.job_id}
                  onSelect={(jobId) => setAssignmentForm({ ...assignmentForm, job_id: jobId })}
                  placeholder="Search for a job..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_start}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_start: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_end}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_end: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Hours per Day</label>
                <input
                  type="text"
                  value={assignmentForm.planned_hours_per_day}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    setAssignmentForm({ ...assignmentForm, planned_hours_per_day: value ? parseFloat(value) : 8 });
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCheckConflicts}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Assignment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Modal */}
      {showConflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Scheduling Conflict Detected
              </h2>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-4">
                This team member is already assigned to other jobs during this period:
              </p>
              <ul className="space-y-2 mb-4">
                {conflicts.map((c, i) => (
                  <li key={i} className="p-3 bg-red-50 rounded-lg text-sm">
                    <span className="font-medium">{c.job_code && `${c.job_code} - `}{c.job_title}</span>
                    <br />
                    <span className="text-slate-600">
                      {format(parseISO(c.assignment_start), 'MMM dd')} - {format(parseISO(c.assignment_end), 'MMM dd, yyyy')}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-500 mb-4">
                Do you want to create this assignment anyway? A supervisor should resolve this conflict.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConflictModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAssignment}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timesheet Entry Modal */}
      {showTimesheetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Log Hours Worked</h2>
              <button onClick={() => setShowTimesheetModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Team Member *</label>
                <TeamMemberSearchSelect
                  members={teamMembers}
                  selectedMemberId={timesheetForm.member_id}
                  onSelect={(memberId) => setTimesheetForm({ ...timesheetForm, member_id: memberId })}
                  placeholder="Search team member..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job *</label>
                <JobSearchSelect
                  jobs={jobs.filter(j => j.status !== 'completed')}
                  selectedJobId={timesheetForm.job_id}
                  onSelect={(jobId) => setTimesheetForm({ ...timesheetForm, job_id: jobId })}
                  placeholder="Search for a job..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date *</label>
                <input
                  type="date"
                  value={timesheetForm.date}
                  onChange={(e) => setTimesheetForm({ ...timesheetForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Regular Hours</label>
                  <input
                    type="text"
                    value={timesheetForm.hours_worked}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setTimesheetForm({ ...timesheetForm, hours_worked: value ? parseFloat(value) : 0 });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Overtime Hours</label>
                  <input
                    type="text"
                    value={timesheetForm.overtime_hours}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setTimesheetForm({ ...timesheetForm, overtime_hours: value ? parseFloat(value) : 0 });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={timesheetForm.description}
                  onChange={(e) => setTimesheetForm({ ...timesheetForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Work performed..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowTimesheetModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTimesheet}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Log Hours
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeAttendance;
